import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowRight, LockKeyhole, RefreshCw } from 'lucide-react';
import magicCardImage from '../assets/home-icons/magic-card.png';
import hallOfFameMedalImage from '../assets/home-icons/hall-of-fame-medal-matte.png';
import libraryMarketImage from '../assets/home-icons/library-market-matte.png';
import MarkdownText from './MarkdownText';
import { t } from '../i18n';
import './HomeLearningPreviews.css';
import './WritingHint.css';
import PracticePoolRecommendation from './PracticePoolRecommendation';
import apiClient from '../services/apiClient';

const INTERVAL_MS = 10000;
const STAGGER_MS = 5000;
const TRANSITION_MS = 420;

function normalizeEntries(entries) {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    if (!entry || entry.id == null || typeof entry.text !== 'string' || !entry.text.trim()) return false;
    const id = String(entry.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }).map(({ id, text, learningLocale, questionText, questionLocale, source, libraryItemId, noteTitle }) => ({
    id: String(id), text, learningLocale,
    questionText: typeof questionText === 'string' && questionText.trim() ? questionText : null,
    questionLocale,
    source: source === 'official' ? 'official' : 'market',
    libraryItemId: libraryItemId != null ? String(libraryItemId) : null,
    noteTitle: typeof noteTitle === 'string' ? noteTitle : '',
  }));
}

function shuffleBag(entries, previousId = null) {
  const bag = [...entries];
  for (let index = bag.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [bag[index], bag[other]] = [bag[other], bag[index]];
  }
  // A fresh round must not start with the card that just ended the last round.
  if (bag.length > 1 && bag[0].id === previousId) {
    [bag[0], bag[1]] = [bag[1], bag[0]];
  }
  return bag;
}

function initialRotation(entries, key) {
  const bag = shuffleBag(entries);
  return { key, current: bag.shift() || null, previous: null, bag, serial: 0 };
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    if (media.addEventListener) media.addEventListener('change', update);
    else media.addListener?.(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', update);
      else media.removeListener?.(update);
    };
  }, []);
  return reduced;
}

function usePageVisibility() {
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return visible;
}

function useInView(ref) {
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !ref.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry) setInView(entry.isIntersecting);
    }, { threshold: 0 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function usePreviewRotation(entries, key, enabled, stagger, reducedMotion, interactionVersion) {
  const [rotation, setRotation] = useState(() => initialRotation(entries, key));
  const remainingRef = useRef(INTERVAL_MS + stagger);
  const poolRef = useRef(entries);
  poolRef.current = entries;

  useEffect(() => {
    remainingRef.current = INTERVAL_MS + stagger;
    setRotation((previous) => previous.key === key ? previous : initialRotation(poolRef.current, key));
  }, [key, stagger]);

  useEffect(() => {
    // A deliberate flip earns a fresh reading interval after hover/focus leaves.
    // This runs after the previous timer's cleanup, before scheduling its replacement.
    if (interactionVersion > 0) remainingRef.current = INTERVAL_MS;
  }, [interactionVersion]);

  useEffect(() => {
    if (!enabled || entries.length < 2 || rotation.key !== key) return undefined;
    const started = Date.now();
    let fired = false;
    const timer = setTimeout(() => {
      fired = true;
      remainingRef.current = INTERVAL_MS;
      setRotation((previous) => {
        const bag = previous.bag.length ? [...previous.bag] : shuffleBag(poolRef.current, previous.current?.id);
        return {
          ...previous,
          current: bag.shift(),
          previous: reducedMotion ? null : previous.current,
          bag,
          serial: previous.serial + 1,
        };
      });
    }, remainingRef.current);
    return () => {
      clearTimeout(timer);
      if (!fired) remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - started));
    };
  }, [enabled, entries.length, key, reducedMotion, rotation.key, rotation.serial, interactionVersion]);

  useEffect(() => {
    if (!rotation.previous) return undefined;
    const timer = setTimeout(() => setRotation((previous) => ({ ...previous, previous: null })), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [rotation.previous, rotation.serial]);

  return rotation.key === key ? rotation : { current: entries[0] || null, previous: null, serial: 0 };
}

function PreviewCard({ kind, entries, count, isLoading, error, onRetry, onOpen, paused, pageVisible, reducedMotion, stagger, writing = false, writingCompletedTodayCount = null, browseLibrary = false, recommendationLevel = 0, recommendationTitle = '', recommendationLabel = '' }) {
  const cardRef = useRef(null);
  const titleId = useId();
  const textId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [face, setFace] = useState({ visitKey: null, question: false });
  const [interactionVersion, setInteractionVersion] = useState(0);
  const inView = useInView(cardRef);
  const poolKey = JSON.stringify(normalizeEntries(entries));
  const pool = useMemo(() => JSON.parse(poolKey), [poolKey]);
  const knownCount = typeof count === 'number' && Number.isFinite(count) && count >= 0 ? count : null;
  const hasRecommendation = recommendationLevel > 0;
  const hasNoteRecommendation = kind === 'library' && Boolean(recommendationTitle);
  const canOpen = writing || browseLibrary || hasRecommendation || hasNoteRecommendation || knownCount > 0;
  const canRotate = !hasRecommendation && canOpen && pool.length > 1 && !isLoading && !error &&
    !paused && !hovered && !focused && pageVisible && inView;
  const rotation = usePreviewRotation(pool, poolKey, canRotate, stagger, reducedMotion, interactionVersion);
  const showText = !isLoading && !error && !hasRecommendation && canOpen && Boolean(rotation.current);
  const isChanging = showText && Boolean(rotation.previous) && !reducedMotion && canRotate;
  const canFlip = showText && Boolean(rotation.current.questionText);
  const visitKey = `${poolKey}:${rotation.serial}`;
  const showingQuestion = canFlip && (face.visitKey === visitKey ? face.question : writing);
  const outgoingQuestion = rotation.previous?.questionText &&
    (face.visitKey === `${poolKey}:${rotation.serial - 1}` ? face.question : writing);
  const nextFaceLabel = t(`dashboard.homePreview.${showingQuestion ? 'showAnswer' : 'showQuestion'}`);
  const Viewport = canFlip ? 'button' : 'div';
  const flip = () => {
    setFace({ visitKey, question: !showingQuestion });
    setInteractionVersion((version) => version + 1);
  };
  const title = writing ? t('writingExam.homeTitle') : hasNoteRecommendation ? recommendationLabel : t(`dashboard.${kind}Title`);
  const artwork = kind === 'levelUp'
    ? magicCardImage
    : kind === 'triumph' ? hallOfFameMedalImage : libraryMarketImage;
  const noPreviewCaption = t(`dashboard.homePreview.${kind === 'library' ? 'libraryNoPreview' : 'noPreview'}`);
  const placeholder = isLoading
    ? t('dashboard.homePreview.loading')
    : error || knownCount === null || canOpen
      ? noPreviewCaption
      : t(`dashboard.homePreview.${kind}Empty`);
  const writingProgressKnown = !isLoading && !error && Number.isInteger(writingCompletedTodayCount) && writingCompletedTodayCount >= 0;
  const countLabel = writing ? !writingProgressKnown
    ? t('writingExam.daily')
    : writingCompletedTodayCount >= 3
      ? t('writingExam.homeComplete')
      : t('writingExam.homeProgress', undefined, { count: writingCompletedTodayCount })
    : hasNoteRecommendation
    ? recommendationTitle
    : hasRecommendation || browseLibrary
    ? t('dashboard.browseLibrary')
    : canOpen
    ? t(`dashboard.${kind}Ready`, undefined, { count: knownCount })
    : isLoading
      ? t('dashboard.homePreview.loading')
      : error
        ? t('dashboard.homePreview.error')
        : knownCount === 0
          ? t(`dashboard.${kind}Locked`)
          : noPreviewCaption;

  const locked = kind === 'triumph' && knownCount === 0 && !isLoading && !error;
  if (locked) {
    return (
      <article className="home-learning-preview-lock" aria-labelledby={titleId}>
        <LockKeyhole size={20} aria-hidden="true" />
        <div>
          <h3 id={titleId}>{title}</h3>
          <p>{t('dashboard.homePreview.triumphEmpty')}</p>
        </div>
      </article>
    );
  }

  return (
    <article
      ref={cardRef}
      className={`home-learning-preview home-learning-preview--${kind}`}
      aria-labelledby={titleId}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <header className="home-learning-preview__header">
        <img src={artwork} alt="" aria-hidden="true" className="home-learning-preview__art" />
        <h3 id={titleId}>{title}</h3>
      </header>

      {error && onRetry ? (
        <div className="home-learning-previews__error" role="alert">
          <span>{t(`dashboard.homePreview.${writing ? 'writingError' : 'libraryError'}`)}</span>
          <button type="button" onClick={onRetry} disabled={isLoading}>
            <RefreshCw size={15} aria-hidden="true" />
            {t('dashboard.homePreview.retry')}
          </button>
        </div>
      ) : <Viewport
        className={`home-learning-preview__viewport${canFlip ? ' home-learning-preview__flip' : ''}${isChanging ? ' is-changing' : ''}`}
        aria-live="off"
        {...(canFlip ? { type: 'button', onClick: flip, 'aria-label': nextFaceLabel, 'aria-describedby': textId } : {})}
      >
        {hasRecommendation ? <PracticePoolRecommendation level={recommendationLevel} /> : showText ? (
          <>
            <span className="home-learning-preview__stage">
              {/* Desktop reserves the full pool height; mobile uses the current face.
                  Hidden sizing cells never repeat content to assistive technology. */}
              <span className="home-learning-preview__measure" aria-hidden="true">
                {pool.flatMap((entry) => [
                  <span key={`${entry.id}-answer`} lang={entry.learningLocale || undefined}><MarkdownText text={entry.text} /></span>,
                  entry.questionText && <span key={`${entry.id}-question`} lang={entry.questionLocale || undefined}><MarkdownText text={entry.questionText} /></span>,
                ])}
              </span>
              {isChanging && (
                <span className="home-learning-preview__outgoing" aria-hidden="true" lang={(outgoingQuestion ? rotation.previous.questionLocale : rotation.previous.learningLocale) || undefined}>
                  <MarkdownText text={outgoingQuestion ? rotation.previous.questionText : rotation.previous.text} />
                </span>
              )}
              <span
                key={`${rotation.current.id}-${rotation.serial}`}
                id={textId}
                className={`home-learning-preview__text${writing && canFlip && !showingQuestion ? ' writing-faint-hint' : ''}`}
                data-entry-id={rotation.current.id}
                data-side={showingQuestion ? 'question' : 'answer'}
                lang={(showingQuestion ? rotation.current.questionLocale : rotation.current.learningLocale) || undefined}
              >
                <MarkdownText text={showingQuestion ? rotation.current.questionText : rotation.current.text} />
              </span>
            </span>
          </>
        ) : <span className="home-learning-preview__placeholder">{placeholder}</span>}
      </Viewport>}

      <button
        type="button"
        className="home-learning-preview__footer"
        aria-label={writing ? t('dashboard.homePreview.writingOpen') : hasNoteRecommendation ? `${t('dashboard.browseLibrary')}: ${recommendationTitle}` : `${title} ${countLabel}`}
        onClick={() => onOpen?.(hasRecommendation || browseLibrary ? null : rotation.current || null)}
        disabled={!canOpen || !onOpen}
      >
        <span className="home-learning-preview__count">{countLabel}</span>
        <ArrowRight size={18} aria-hidden="true" />
      </button>
    </article>
  );
}

export default function HomeLearningPreviews({
  levelUp = [], triumph = [], library = [], upgradeableCount, completedCount, libraryCount,
  libraryRecommendationEntry = null, libraryRecommendationTitle = '', libraryRecommendationCount = 0,
  libraryRecommendationLabel = '',
  isLoading = false, error = null, isLibraryLoading = false, libraryError = null,
  onRetry, onLibraryRetry = onRetry, onLevelUp, onTriumph, onLibrary, paused = false,
  practicePoolWarningLevel = 0, libraryOnly = false, writingPractice = false, afterWritingContent = null, trailingContent = null,
}) {
  const reducedMotion = useReducedMotion();
  const pageVisible = usePageVisibility();
  const sectionTitleId = useId();
  const shared = { paused, pageVisible, reducedMotion };
  const [writingQuestions, setWritingQuestions] = useState([]);
  const [writingCompletedTodayCount, setWritingCompletedTodayCount] = useState(null);
  const [writingLoading, setWritingLoading] = useState(false);
  const [writingError, setWritingError] = useState(null);
  const [writingRefresh, setWritingRefresh] = useState(0);
  useEffect(() => {
    if (!writingPractice || libraryOnly) return undefined;
    let active = true;
    let inFlight = false;
    const load = async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      setWritingLoading(true);
      setWritingError(null);
      setWritingCompletedTodayCount(null);
      try {
        const result = await apiClient.getWritingToday();
        if (!Array.isArray(result?.questions)) throw new Error('Invalid writing preview response');
        if (active) {
          setWritingCompletedTodayCount(result.completedTodayCount ?? null);
          setWritingQuestions(result.questions.map(entry => entry.answerText ? {
            ...entry, questionText: entry.text, questionLocale: entry.learningLocale,
            text: entry.answerText, learningLocale: entry.answerLocale,
          } : entry));
        }
      } catch (err) {
        if (active) { setWritingQuestions([]); setWritingError(err); }
      } finally { inFlight = false; if (active) setWritingLoading(false); }
    };
    void load();
    document.addEventListener('visibilitychange', load);
    return () => { active = false; document.removeEventListener('visibilitychange', load); };
  }, [writingPractice, libraryOnly, writingRefresh]);
  const hasError = Boolean(error);
  const triumphLocked = completedCount === 0 && !isLoading && !error;
  return (
    <section className="home-learning-previews" aria-label={t('dashboard.homePreview.sectionLabel')}>
      {libraryOnly && <h2 className="home-learning-previews__label">{t('dashboard.library')}</h2>}
      {hasError && (
        <div className="home-learning-previews__error" role="alert">
          <span>{t('dashboard.homePreview.error')}</span>
          {onRetry && (
            <button type="button" onClick={onRetry} disabled={isLoading || isLibraryLoading}>
              <RefreshCw size={15} aria-hidden="true" />
              {t('dashboard.homePreview.retry')}
            </button>
          )}
        </div>
      )}
      <div className="home-learning-previews__grid">
        {!libraryOnly && writingPractice && <PreviewCard {...shared} kind="levelUp" writing writingCompletedTodayCount={writingCompletedTodayCount} entries={writingQuestions} count={writingQuestions.length} isLoading={writingLoading} error={writingError} onRetry={() => setWritingRefresh(value => value + 1)} onOpen={onLevelUp} stagger={0} />}
        {!libraryOnly && !writingPractice && <PreviewCard {...shared} kind="levelUp" entries={levelUp} count={upgradeableCount} isLoading={isLoading} error={error} onOpen={onLevelUp} stagger={0} />}
        {!libraryOnly && writingPractice && afterWritingContent}
        {practicePoolWarningLevel > 0 && <PracticePoolRecommendation level={practicePoolWarningLevel} />}
        <PreviewCard {...shared} kind="library" browseLibrary={practicePoolWarningLevel > 0} entries={libraryRecommendationEntry && !library.length ? [libraryRecommendationEntry] : library} count={libraryRecommendationCount || libraryCount} isLoading={isLibraryLoading} error={libraryError} onRetry={onLibraryRetry} onOpen={onLibrary} stagger={STAGGER_MS / 2} recommendationTitle={libraryRecommendationTitle} recommendationLabel={libraryRecommendationLabel} />
        {triumphLocked && trailingContent}
        {!libraryOnly && <div className="home-learning-previews__triumph"><h2 id={sectionTitleId} className="home-learning-previews__label">{t('dashboard.homePreview.label')}</h2><PreviewCard {...shared} kind="triumph" entries={triumph} count={completedCount} isLoading={isLoading} error={error} onOpen={onTriumph} stagger={STAGGER_MS} /></div>}
        {!triumphLocked && trailingContent}
      </div>
    </section>
  );
}
